import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Calendar, Users, ExternalLink, Video, Wrench, ArrowRight,
  ChevronDown, ChevronUp, Clock, X, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const STATE_TABS = [
  { k: "scheduled", l: "Scheduled" },
  { k: "ongoing", l: "Live now" },
  { k: "completed", l: "Past events" },
];

function timeUntil(iso) {
  const d = new Date(iso) - new Date();
  if (d <= 0) return "starting now";
  const m = Math.floor(d / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h ${m % 60}m`;
  const days = Math.floor(h / 24);
  if (days < 14) return `in ${days}d ${h % 24}h`;
  return new Date(iso).toLocaleDateString();
}

function StateBadge({ state }) {
  if (state === "ongoing") {
    return (
      <span className="kb-mono text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-rose-600 rounded-full animate-pulse" /> LIVE NOW
      </span>
    );
  }
  if (state === "completed") return <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">COMPLETED</span>;
  return <span className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700">SCHEDULED</span>;
}

function RegisterModal({ event, onClose, onRegistered }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.name || "", affiliation: user?.affiliation || "",
    role: user?.role || "researcher", motivation: "", questions_for_host: "",
    consent_recording: true,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/events/${event.id}/register`, form);
      toast.success(r.data?.already_registered ? "Already registered — see you there!" : "You're registered");
      onRegistered?.(r.data?.registration);
    } catch (e) {
      // Paid event — redirect to payment
      if (e?.response?.status === 402) {
        const d = e.response.data || {};
        toast.message("Redirecting to payment", { description: `NPR ${d.amount_npr?.toLocaleString()}` });
        navigate(`/payments/checkout?context_type=event&context_id=${event.id}`);
        onClose?.();
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to register");
    } finally { setBusy(false); }
  };

  const modal = (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="event-register-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">REGISTER</div>
            <h3 className="font-serif text-xl text-kb-ink mt-1 leading-tight">{event.title}</h3>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
              {new Date(event.scheduled_at).toLocaleString()} · {event.duration_min}m · {event.is_free ? "FREE" : `NPR ${event.price_npr?.toLocaleString()}`}
            </div>
          </div>
          <button onClick={onClose} className="text-kb-ink-muted hover:text-kb-ink p-1" data-testid="event-register-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Full name *</label>
            <input className="kb-input text-sm w-full mt-1" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="event-reg-name" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Affiliation / Institution</label>
            <input className="kb-input text-sm w-full mt-1" placeholder="e.g. Tribhuvan University"
              value={form.affiliation} onChange={(e) => setForm({ ...form, affiliation: e.target.value })}
              data-testid="event-reg-affiliation" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">I am a</label>
            <select className="kb-input text-sm w-full mt-1" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="event-reg-role">
              <option value="student">Student</option>
              <option value="researcher">Researcher</option>
              <option value="faculty">Faculty</option>
              <option value="professional">Industry professional</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">What do you hope to learn?</label>
            <textarea rows={2} className="kb-input text-sm w-full mt-1 resize-none"
              value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })}
              data-testid="event-reg-motivation" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Questions for the host (optional)</label>
            <textarea rows={2} className="kb-input text-sm w-full mt-1 resize-none"
              value={form.questions_for_host} onChange={(e) => setForm({ ...form, questions_for_host: e.target.value })}
              data-testid="event-reg-questions" />
          </div>
          <label className="flex items-start gap-2 text-xs text-kb-ink-muted pt-2">
            <input type="checkbox" checked={form.consent_recording}
              onChange={(e) => setForm({ ...form, consent_recording: e.target.checked })} className="mt-0.5" />
            <span>I consent to this session being recorded for educational use.</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-2 justify-end sticky bottom-0 bg-white">
          <button onClick={onClose} className="kb-btn-outline text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="kb-btn-primary text-sm" data-testid="event-reg-submit">
            {busy ? "Submitting…" : event.is_free ? "Confirm registration" : `Continue to payment (NPR ${event.price_npr?.toLocaleString()})`}
          </button>
        </div>
      </div>
    </div>
  );
  // Portal to body so no ancestor transform/overflow can clip the overlay
  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

function EventCard({ event, onRegister, onChange }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const Icon = event.type === "workshop" ? Wrench : Video;
  const state = event.state;
  const registered = event.is_registered;

  const promptRegister = () => {
    if (!user) { toast.error("Please sign in to register"); return; }
    onRegister(event);
  };

  return (
    <article className="kb-card overflow-hidden" data-testid={`public-event-${event.id}`}>
      {event.cover_image_url && (
        <div className="h-32 overflow-hidden bg-slate-100">
          <img src={event.cover_image_url} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">{event.type}</span>
          <StateBadge state={state} />
          {event.is_free
            ? <span className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700 ml-auto">FREE</span>
            : <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary ml-auto">NPR {event.price_npr?.toLocaleString()}</span>}
        </div>
        <h3 className="font-serif text-xl text-kb-ink mt-3 leading-tight">{event.title}</h3>
        {event.description && (
          <p className={`text-sm text-kb-ink-muted mt-2 ${expanded ? "" : "line-clamp-2"}`}>
            {event.description}
          </p>
        )}

        <div className="mt-4 pt-4 border-t border-slate-200 space-y-1.5 text-xs text-kb-ink-muted">
          <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(event.scheduled_at).toLocaleString()} · {event.duration_min} min</div>
          {state === "scheduled" && <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> Starts {timeUntil(event.scheduled_at)}</div>}
          {event.host_name && <div>Hosted by <strong className="text-kb-ink">{event.host_name}</strong></div>}
          <div className="flex items-center gap-1.5"><Users className="w-3 h-3" /> {event.registrations || 0} registered{event.capacity ? ` / ${event.capacity}` : ""}</div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-200 space-y-2 text-sm">
            {event.agenda && (
              <div>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">AGENDA</div>
                <p className="text-kb-ink whitespace-pre-wrap mt-1">{event.agenda}</p>
              </div>
            )}
            {event.prerequisites && (
              <div>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">PREREQUISITES</div>
                <p className="text-kb-ink whitespace-pre-wrap mt-1">{event.prerequisites}</p>
              </div>
            )}
            {event.tags?.length > 0 && (
              <div className="flex gap-1.5 flex-wrap pt-1">
                {event.tags.map((t) => <span key={t} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted border border-slate-200 px-2 py-0.5 rounded-sm">{t}</span>)}
              </div>
            )}
          </div>
        )}

        <button onClick={() => setExpanded((v) => !v)}
          className="mt-3 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-1"
          data-testid={`event-${event.id}-toggle`}>
          {expanded ? <>Hide details <ChevronUp className="w-3 h-3" /></> : <>Show details <ChevronDown className="w-3 h-3" /></>}
        </button>

        <div className="mt-4 flex gap-2">
          {state === "completed" ? (
            <button disabled className="kb-btn-outline text-sm w-full justify-center opacity-50 cursor-not-allowed">Event ended</button>
          ) : registered ? (
            <>
              <div className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2 rounded-sm" data-testid={`event-${event.id}-registered-pill`}>
                <CheckCircle2 className="w-4 h-4" /> You're registered
              </div>
              {state === "ongoing" && event.my_registration?.event_id && (
                <RegisteredJoinButton event={event} />
              )}
            </>
          ) : (
            <button onClick={promptRegister} className="kb-btn-primary text-sm w-full justify-center" data-testid={`event-${event.id}-register`}>
              {event.is_free ? <>Register <ArrowRight className="w-3.5 h-3.5" /></> : <>Register · NPR {event.price_npr?.toLocaleString()}</>}
            </button>
          )}
        </div>



      </div>
    </article>
  );
}

function RegisteredJoinButton({ event }) {
  // After registration the public-list endpoint hides meet_url for privacy;
  // fetch the single-event endpoint (Authorization is added by axios interceptor)
  // to obtain the join URL only for the registered user.
  const [meetUrl, setMeetUrl] = useState("");
  useEffect(() => {
    api.get(`/events/${event.id}`).then((r) => {
      if (!r.data?.meet_url_locked) setMeetUrl(r.data?.meet_url || "");
    }).catch(() => {});
  }, [event.id]);
  if (!meetUrl) return null;
  return (
    <a href={meetUrl} target="_blank" rel="noreferrer"
      className="kb-btn-primary text-sm justify-center" data-testid={`event-${event.id}-join`}>
      <ExternalLink className="w-3.5 h-3.5" /> Join
    </a>
  );
}

export default function EventsPublic() {
  const { user } = useAuth();
  const [tab, setTab] = useState("scheduled");
  const [type, setType] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingEvent, setPendingEvent] = useState(null); // single source of truth for the registration modal

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/events", { params: { state: tab, ...(type ? { event_type: type } : {}) } });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, type]);

  // Body scroll-lock while modal is open (prevents flicker due to layout reflow)
  useEffect(() => {
    if (pendingEvent) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [pendingEvent]);

  return (
    <div className="bg-kb-bg min-h-screen" data-testid="events-public-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 pt-16 pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">WEBINARS & WORKSHOPS</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-2">
          Live <em className="italic text-kb-primary">research events</em>.
        </h1>
        <p className="text-kb-ink-muted mt-3 max-w-2xl">
          Free and paid sessions with senior researchers, methodologists, and tooling experts. Register early — capacity is limited.
        </p>

        <div className="mt-8 border-b border-slate-200 flex gap-1">
          {STATE_TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.k ? "border-kb-primary text-kb-primary font-medium" : "border-transparent text-kb-ink-muted hover:text-kb-primary"}`}
              data-testid={`events-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          {[{ v: "", l: "All" }, { v: "webinar", l: "Webinars" }, { v: "workshop", l: "Workshops" }].map((opt) => (
            <button key={opt.v} onClick={() => setType(opt.v)}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${type === opt.v ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"}`}>
              {opt.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            {tab === "completed" ? "No past events to show yet." : tab === "ongoing" ? "Nothing live right now — check back soon." : "No upcoming events. New sessions get added every week."}
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((e) => <EventCard key={e.id} event={e} onRegister={setPendingEvent} onChange={load} />)}
          </div>
        )}
        {!user && (
          <div className="mt-10 text-center text-sm text-kb-ink-muted">
            <Link to="/login" className="text-kb-primary">Sign in</Link> or <Link to="/onboarding/role" className="text-kb-primary">create an account</Link> to register for events.
          </div>
        )}
      </main>
      {/* Modal lives at page root — fixed overlay, no flicker on card re-render */}
      {pendingEvent && (
        <RegisterModal
          event={pendingEvent}
          onClose={() => setPendingEvent(null)}
          onRegistered={() => { setPendingEvent(null); load(); }}
        />
      )}
      <Footer />
    </div>
  );
}
